import { graphql } from '@/__generated__';
import { ModelAppliedStateNode } from '@/__generated__/graphql';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useQueryStable } from '../useQueryStable';

type Result = ReturnType<typeof useSchemaByEnvironment>[0];

export type SchemaResponse = NonNullable<Result['data']>;

export const GetSchemaByUniqueId = graphql(/* GraphQL */ `
  query Schema($environmentId: BigInt!, $uniqueIds: [String!]) {
    environment(id: $environmentId) {
      applied {
        lastUpdatedAt
        resources(
          first: 1
          filter: { uniqueIds: $uniqueIds, types: [Model, Snapshot, Seed, Source] }
        ) {
          edges {
            node {
              ... on ModelAppliedStateNode {
                schema
                database
              }
              ... on SnapshotAppliedStateNode {
                schema
                database
              }
              ... on SeedAppliedStateNode {
                schema
                database
              }
              ... on SourceAppliedStateNode {
                schema
                database
              }
            }
          }
        }
      }
    }
  }
`);

type SchemaByEnvironmentParams = {
  environmentId: number | undefined;
  uniqueId?: string;
  pause?: boolean;
};

export const useSchemaByEnvironment = ({
  environmentId = 0,
  uniqueId,
  pause,
}: SchemaByEnvironmentParams) => {
  const query = useQueryStable({
    query: GetSchemaByUniqueId,
    variables: { environmentId, uniqueIds: uniqueId ? [uniqueId] : [] },
    pause: pause || !environmentId || !uniqueId,
  });

  useCacheMonitor({
    applied: query[0].data?.environment.applied?.lastUpdatedAt,
  });

  return query;
};

export const useSchemaForUniqueId = (uniqueId: string | undefined) => {
  const { environmentId } = useDbtCloudProject();
  const [schemaQuery] = useSchemaByEnvironment({ environmentId, uniqueId });
  if (schemaQuery.fetching) return;
  const edges = schemaQuery.data?.environment.applied?.resources.edges;
  if (!edges?.length || !edges[0]) return;
  const { node } = edges[0];
  const typecastNode = node as ModelAppliedStateNode;
  const schema = typecastNode.schema;
  const database = typecastNode.database;

  if (schema && database) {
    return { schema, database };
  }
  return;
};
